'use client';

import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, limit, total, onPageChange }: PaginationProps) {
  // Ensure all values are valid numbers
  const safePage = Number(page) || 1;
  const safeLimit = Number(limit) || 10;
  const safeTotal = Number(total) || 0;
  
  const totalPages = Math.ceil(safeTotal / safeLimit);
  const canGoPrevious = safePage > 1;
  const canGoNext = safePage < totalPages;

  if (safeTotal === 0) return null;

  const startItem = Math.min((safePage - 1) * safeLimit + 1, safeTotal);
  const endItem = Math.min(safePage * safeLimit, safeTotal);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-2.5 bg-gray-50/80 border-t border-gray-100"
    >
      <p className="text-xs text-gray-500 order-2 sm:order-1">
        <span className="font-medium text-gray-600">{startItem}</span>
        {' - '}
        <span className="font-medium text-gray-600">{endItem}</span>
        {' of '}
        <span className="font-medium text-gray-600">{safeTotal}</span>
      </p>
      
      <div className="flex items-center gap-1.5 order-1 sm:order-2">
        <motion.button
          whileHover={{ scale: canGoPrevious ? 1.05 : 1 }}
          whileTap={{ scale: canGoPrevious ? 0.95 : 1 }}
          onClick={() => canGoPrevious && onPageChange(safePage - 1)}
          disabled={!canGoPrevious}
          className={`
            inline-flex items-center gap-0.5 px-2 py-1.5 text-xs font-medium rounded-lg
            transition-all duration-200
            ${canGoPrevious 
              ? 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 shadow-sm' 
              : 'text-gray-400 bg-gray-100 border border-gray-100 cursor-not-allowed'}
          `}
        >
          <ChevronLeft size={14} />
          <span className="hidden sm:inline">Prev</span>
        </motion.button>
        
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = safePage <= 3 ? i + 1 : safePage - 2 + i;
            if (pageNum > totalPages || pageNum < 1) return null;
            return (
              <motion.button
                key={pageNum}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onPageChange(pageNum)}
                className={`
                  w-7 h-7 rounded-lg text-xs font-medium transition-all duration-200
                  ${pageNum === safePage 
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm' 
                    : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'}
                `}
              >
                {pageNum}
              </motion.button>
            );
          })}
        </div>
        
        <motion.button
          whileHover={{ scale: canGoNext ? 1.05 : 1 }}
          whileTap={{ scale: canGoNext ? 0.95 : 1 }}
          onClick={() => canGoNext && onPageChange(safePage + 1)}
          disabled={!canGoNext}
          className={`
            inline-flex items-center gap-0.5 px-2 py-1.5 text-xs font-medium rounded-lg
            transition-all duration-200
            ${canGoNext 
              ? 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 shadow-sm' 
              : 'text-gray-400 bg-gray-100 border border-gray-100 cursor-not-allowed'}
          `}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight size={14} />
        </motion.button>
      </div>
    </motion.div>
  );
}
